/**
 * openInApp — 모바일 웹 / 인앱 브라우저(카카오톡 등)에서 설치된 TEMPLATE 앱을 직접 실행.
 *
 * 배경:
 *   카카오톡 인앱 브라우저는 https App Link 를 OS 로 넘기지 않고 자체 webview 로 연다.
 *   → 앱이 설치돼 있어도 App Link 가 안 터지고 웹 페이지에 갇힌다.
 *   해결: custom scheme(iOS) / Android intent:// 로 앱을 직접 호출한다.
 *   인앱 브라우저 감지/탈출은 ./inAppBrowser.ts 참조.
 *
 * 환경값:
 *   - VITE_APP_DEEP_LINK_SCHEME (기본 "peeka") — 앱 custom scheme.
 *   - VITE_ANDROID_PACKAGE (기본 dev 패키지) — dev=...app.dev / prod=...app.
 *   - VITE_IOS_APP_STORE_ID — App Store 숫자 ID. 미설정 시 iOS 미설치 폴백은
 *     스토어 이동 대신 onIosNotInstalled 콜백만 호출한다 (앱 출시 후 주입).
 *     앱의 deep-link 라우터(RootLayout.handleDeepLink)가 peeka://gift/receive?token=...
 *     를 /gift/receive?token=... 로 navigate 한다.
 */
import { detectInAppBrowser, escapeInAppBrowser } from "./inAppBrowser";

const SCHEME =
  (import.meta.env.VITE_APP_DEEP_LINK_SCHEME as string | undefined)?.trim() || "peeka";

const ANDROID_PACKAGE =
  (import.meta.env.VITE_ANDROID_PACKAGE as string | undefined)?.trim() ||
  "kr.duckflex.peeka.app.dev";

const IOS_APP_STORE_ID =
  (import.meta.env.VITE_IOS_APP_STORE_ID as string | undefined)?.trim() || "";

/** iOS 스킴 실행 후 앱 전환(visibility hidden)이 감지되지 않으면 미설치로 판단하는 대기 시간. */
const IOS_FALLBACK_TIMEOUT_MS = 2000;
/** 백그라운드 탭에서 뒤늦게 깨어난 타이머의 오발사(앱 갔다 복귀 직후 스토어행) 방지 여유. */
const IOS_FALLBACK_SLACK_MS = 500;

export type MobileOS = "ios" | "android" | "other";

export function detectMobileOS(): MobileOS {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

/** App Store 페이지 URL. VITE_IOS_APP_STORE_ID 미설정(미출시)이면 null. */
export function iosAppStoreUrl(): string | null {
  return IOS_APP_STORE_ID ? `https://apps.apple.com/kr/app/id${IOS_APP_STORE_ID}` : null;
}

/**
 * iOS 앱 제공 여부 — App Store 출시 후 VITE_IOS_APP_STORE_ID 주입 시 true.
 * false 면 iOS 에서 딥링크/자동탈출을 시도하지 않고 호출측이 "미지원 안내"를 띄운다.
 * (개발자 계정 승인 대기 중 — iOS 앱 출시 시 env 주입만으로 전체 플로우 활성화.)
 */
export function isIosAppAvailable(): boolean {
  return IOS_APP_STORE_ID !== "";
}

/**
 * Play Store 상세 페이지 URL.
 * @param referrer  install referrer 페이로드 (예: "gift_token=abc").
 *                  설치 후 앱이 Install Referrer API 로 읽어 디퍼드 딥링크에 사용.
 */
export function playStoreUrl(referrer?: string): string {
  const base = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
  return referrer ? `${base}&referrer=${encodeURIComponent(referrer)}` : base;
}

interface OpenInAppOptions {
  /** Android 에서 앱 미설치 시 이동할 URL (기본: 해당 패키지의 Play Store). */
  androidFallbackUrl?: string;
  /** Play Store 폴백 URL 에 붙일 install referrer (androidFallbackUrl 지정 시 무시). */
  playReferrer?: string;
  /**
   * iOS 미설치 판정(스킴 실행 후에도 페이지가 보임) 시 호출.
   * App Store ID 가 설정돼 있으면 스토어로 이동하므로 호출되지 않는다.
   */
  onIosNotInstalled?: () => void;
  /**
   * false 면 iOS 에서 스킴만 실행하고 미설치 폴백(스토어 이동/콜백)을 생략한다.
   * 탈출 불가 인앱 브라우저(네이버/인스타 iOS)처럼 호출측이 이미 안내 UI 를 띄운 경우,
   * 뒤늦은 스토어 이동이 안내를 덮어버리는 것을 막는다. (기본 true)
   */
  iosStoreFallback?: boolean;
}

/**
 * iOS — custom scheme 실행 + 미설치 폴백.
 *
 * 스킴 실행 후 IOS_FALLBACK_TIMEOUT_MS 안에 앱 전환(pagehide / visibility hidden)이
 * 없으면 미설치로 판단해 App Store 로 이동한다. 앱이 떠서 페이지가 가려지면 타이머를
 * 취소해, 사용자가 앱에서 돌아왔을 때 스토어로 튕기지 않게 한다.
 */
function openIosWithStoreFallback(path: string, onNotInstalled?: () => void): void {
  const startedAt = Date.now();

  const cleanup = () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
  };
  const cancel = () => {
    window.clearTimeout(timer);
    cleanup();
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") cancel();
  };
  const onPageHide = () => cancel();

  const timer = window.setTimeout(() => {
    cleanup();
    // 앱 전환 중(페이지 가려짐) 또는 백그라운드에서 지연 발사된 타이머면 폴백하지 않는다.
    // 한계: 느린 기기에서 visibility 이벤트가 앱 전환보다 늦게 도착하면 드물게 오발사
    // 가능 (WebKit 특성상 완전 제거 불가). 타임아웃 2s 는 그 빈도를 낮추기 위한 값.
    if (document.visibilityState === "hidden") return;
    if (Date.now() - startedAt > IOS_FALLBACK_TIMEOUT_MS + IOS_FALLBACK_SLACK_MS) return;
    const store = iosAppStoreUrl();
    if (store) window.location.href = store;
    else onNotInstalled?.();
  }, IOS_FALLBACK_TIMEOUT_MS);

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);

  window.location.href = `${SCHEME}://${path}`;
}

/**
 * 설치된 앱을 내부 경로로 실행 시도.
 *
 * @param appPath  앱 내부 경로 (예: "gift/receive?token=abc"). 선행 슬래시는 무시됨.
 * @returns        시도한 플랫폼. "other"(데스크톱 등)면 아무 동작도 하지 않는다.
 *
 * 동작:
 *   - Android: intent://... (미설치 시 browser_fallback_url 로 자동 이동 = Play Store)
 *   - iOS:     peeka://... + 미설치 판정 시 App Store 폴백 (visibility 기반)
 */
export function openInApp(appPath: string, opts?: OpenInAppOptions): MobileOS {
  const os = detectMobileOS();
  const path = appPath.replace(/^\/+/, "");

  if (os === "android") {
    const fallback = opts?.androidFallbackUrl ?? playStoreUrl(opts?.playReferrer);
    const intentUrl =
      `intent://${path}#Intent;scheme=${SCHEME};package=${ANDROID_PACKAGE};` +
      `S.browser_fallback_url=${encodeURIComponent(fallback)};end`;
    window.location.href = intentUrl;
  } else if (os === "ios") {
    if (opts?.iosStoreFallback === false) {
      window.location.href = `${SCHEME}://${path}`;
    } else {
      openIosWithStoreFallback(path, opts?.onIosNotInstalled);
    }
  }

  return os;
}

/**
 * 페이지 로드 시 앱 자동 실행 시도 (탭 없이).
 *
 * - Android: intent:// 직접 실행. 카카오/네이버/인스타 인앱 webview 안에서도 대체로 동작.
 *   미설치 시 스토어로 튕기지 않도록 fallback = 현재 페이지(자기 자신) → 그 자리에 머무름.
 * - iOS: 제스처 없는 custom scheme 실행은 webview 차단 또는 미설치 시 "주소가 잘못됐다"
 *   에러 팝업 위험이 있어 시도하지 않는다. 단, 카카오톡/LINE 인앱은 자체 탈출 스킴이
 *   안전하므로 외부 브라우저로 자동 탈출한다 — 앱 설치 시 OS 가 Universal Link 로 앱을
 *   바로 띄우고, 미설치 시 외부 브라우저에서 이 랜딩이 다시 열린다.
 * - sessionStorage 가드로 dedupeKey 당 1회만 (재방문 / 스토어·앱 복귀 시 무한 바운스 방지).
 *
 * @returns 자동 실행을 시도했으면 true, 아니면 false.
 */
export function autoOpenInApp(appPath: string, dedupeKey: string): boolean {
  const os = detectMobileOS();
  const inAppBrowser = detectInAppBrowser();
  const canAuto =
    os === "android" ||
    (os === "ios" &&
      isIosAppAvailable() &&
      (inAppBrowser === "kakaotalk" || inAppBrowser === "line"));
  if (!canAuto) return false;

  try {
    const k = `pkr-autoopen-${dedupeKey}`;
    if (sessionStorage.getItem(k)) return false;
    sessionStorage.setItem(k, "1");
  } catch {
    // private 모드 등 sessionStorage 불가 — 가드 없이 1회 시도 진행.
  }

  if (os === "ios") {
    return escapeInAppBrowser(window.location.href);
  }

  openInApp(appPath, {
    androidFallbackUrl: typeof window !== "undefined" ? window.location.href : undefined,
  });
  return true;
}
